import { useEffect } from "react";

/**
 * Renders the standalone privacy policy HTML file as a full React page.
 * The same content is also available as a plain HTML file at
 * /app/memory/getarvo_privacy_policy.html for handing to whoever hosts the
 * production site (CMS upload route).
 */
export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy · Arvo";
  }, []);

  return (
    <main
      data-testid="privacy-page"
      className="min-h-screen bg-[#F9F8F6] text-[#1A1D1A] py-12"
    >
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 leading-relaxed">
        <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#D96C32]">
          Arvo
        </p>
        <h1 className="mt-1 font-display font-bold text-4xl sm:text-5xl tracking-tight">
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm text-[#4A4F4A]">
          Effective date: 17 December 2025 · Last updated: 17 December 2025
        </p>

        <p className="mt-6">
          Arvo (&quot;<strong>we</strong>&quot;, &quot;<strong>our</strong>&quot;, &quot;
          <strong>us</strong>&quot;) is a New Zealand–based livestock data capture tool,
          delivered as a website at{" "}
          <a className="text-[#2D4A22] font-semibold" href="https://www.getarvo.co.nz">
            getarvo.co.nz
          </a>{" "}
          and as a mobile application available on the Apple App Store and Google Play.
          This Privacy Policy explains what information we collect, how we use it, and the
          choices you have. It applies to the Arvo website and the Arvo mobile app
          (together, the &quot;<strong>Service</strong>&quot;).
        </p>

        <p className="mt-4">
          We comply with the New Zealand <strong>Privacy Act 2020</strong>. We take a
          deliberately minimal approach to data collection — if we don&apos;t need it, we
          don&apos;t ask for it, and we don&apos;t store it.
        </p>

        <h2 className="mt-10 font-display font-bold text-xl">1. The short version</h2>
        <ul className="mt-3 list-disc pl-6 space-y-1.5">
          <li>
            <strong>Your animal data stays on your phone.</strong> Arvo is independent and
            provider-neutral. We do not sync, upload or store the livestock records you
            capture in the app on our servers. You stay in control — when you want to share,
            you export the CSV yourself.
          </li>
          <li>
            <strong>We don&apos;t sell your data.</strong> Ever. Not to advertisers, data
            brokers, herd record providers or anyone else.
          </li>
          <li>
            <strong>Website analytics are anonymous.</strong> No IP addresses, no cookies,
            no ad tracking — just session counts and page paths.
          </li>
          <li>
            <strong>You can request deletion at any time</strong> by emailing{" "}
            <a className="text-[#2D4A22] font-semibold" href="mailto:support@getarvo.co.nz">
              support@getarvo.co.nz
            </a>
            .
          </li>
        </ul>

        <h2 className="mt-10 font-display font-bold text-xl">
          2. Information we collect
        </h2>

        <h3 className="mt-5 font-display font-bold text-base">
          2.1 Information you give us (website)
        </h3>
        <p className="mt-2">
          If you submit a form on{" "}
          <a className="text-[#2D4A22] font-semibold" href="https://www.getarvo.co.nz">
            getarvo.co.nz
          </a>{" "}
          we collect what you choose to share:
        </p>
        <ul className="mt-2 list-disc pl-6 space-y-1.5">
          <li>
            <strong>Demo request form:</strong> your name, email address, farm type,
            optional herd size and any message you provide.
          </li>
          <li>
            <strong>Contact form:</strong> your name, email address and message.
          </li>
          <li>
            <strong>Early-access list:</strong> your email address, optional name and farm
            type.
          </li>
        </ul>

        <h3 className="mt-5 font-display font-bold text-base">
          2.2 Information collected automatically (website)
        </h3>
        <p className="mt-2">
          We use a small internal counter to understand how many people visit the site. It
          records:
        </p>
        <ul className="mt-2 list-disc pl-6 space-y-1.5">
          <li>An anonymous, random session identifier (not a cookie)</li>
          <li>Which page was viewed</li>
          <li>The referring website&apos;s hostname (e.g. &quot;google.com&quot;) — never the full URL</li>
          <li>Device type (mobile, tablet or desktop)</li>
        </ul>
        <p className="mt-3">
          We do <strong>not</strong> collect IP addresses, do <strong>not</strong> set
          tracking cookies, and do <strong>not</strong> use third-party analytics or
          advertising networks.
        </p>

        <h3 className="mt-5 font-display font-bold text-base">
          2.3 Information collected by the Arvo mobile app
        </h3>
        <p className="mt-2">
          The Arvo mobile app uses certain device features to do its job in the yard. None
          of the livestock records you capture in-app are transmitted to Arvo&apos;s servers
          — they remain stored locally on your device.
        </p>
        <ul className="mt-2 list-disc pl-6 space-y-1.5">
          <li>
            <strong>Imported animal records:</strong> when you import a CSV (e.g. from your
            herd record provider or NAIT records), the file is stored locally on your
            device and used only to perform the workflows you&apos;ve chosen.
          </li>
          <li>
            <strong>Location (GPS):</strong> with your permission, the app can record the
            GPS coordinates of a session so you can verify where work took place. Location
            data is stored locally on your device as part of the session record and is not
            sent to us.
          </li>
          <li>
            <strong>Bluetooth:</strong> with your permission, the app pairs with compatible
            EID readers and Bluetooth scanners (used to read QR codes) during a session.
          </li>
          <li>
            <strong>Storage:</strong> with your permission, the app reads and writes
            session files (CSV import and export) in your device storage.
          </li>
          <li>
            <strong>Network access:</strong> the app uses the internet to check for app
            updates and to send anonymous, aggregated usage statistics (e.g. the app was
            opened, a session was completed). These statistics never include the contents
            of your records or the identity of your animals.
          </li>
        </ul>

        <h3 className="mt-5 font-display font-bold text-base">
          2.4 Information we don&apos;t collect
        </h3>
        <ul className="mt-2 list-disc pl-6 space-y-1.5">
          <li>We don&apos;t ask you to create an account to use the app.</li>
          <li>We don&apos;t collect your IP address on the website.</li>
          <li>We don&apos;t track you across other apps or websites.</li>
          <li>We don&apos;t sell, rent or share your personal information.</li>
        </ul>

        <h2 className="mt-10 font-display font-bold text-xl">3. How we use information</h2>
        <p className="mt-2">We only use information for the reasons it was collected:</p>
        <ul className="mt-2 list-disc pl-6 space-y-1.5">
          <li>
            <strong>To respond to your enquiry</strong> — if you fill in a contact, demo or
            early-access form, we use the details you&apos;ve provided to reply to you and
            (for the early-access list) to email you when the app is publicly available.
          </li>
          <li>
            <strong>To run the app on your device</strong> — local permissions (location,
            Bluetooth, storage) are used only to deliver the functionality you&apos;ve
            asked for, such as recording weights, scanning EID tags or exporting a CSV.
          </li>
          <li>
            <strong>To improve Arvo</strong> — anonymous usage statistics help us understand
            which features are used so we can make the app better. These statistics cannot
            be tied back to you.
          </li>
          <li>
            <strong>To keep Arvo secure</strong> — to detect and prevent abuse of our forms
            (for example, automated spam).
          </li>
        </ul>

        <h2 className="mt-10 font-display font-bold text-xl">4. Sharing of information</h2>
        <p className="mt-2">
          We don&apos;t share your personal information with third parties for marketing
          purposes. We only share information in the limited cases below:
        </p>
        <ul className="mt-2 list-disc pl-6 space-y-1.5">
          <li>
            <strong>Service providers we rely on</strong> — such as our website host and
            the email service we use to reply to you. These providers process information
            only on our instructions and only to the extent needed to deliver their
            service.
          </li>
          <li>
            <strong>App store providers</strong> — when you install or use the Arvo mobile
            app, Apple and Google may collect certain device information under their own
            privacy policies (for example, crash reports and install metrics). Arvo does
            not control this and you should review the privacy policies of{" "}
            <a className="text-[#2D4A22] font-semibold" href="https://www.apple.com/legal/privacy/en-ww/">
              Apple
            </a>{" "}
            and{" "}
            <a className="text-[#2D4A22] font-semibold" href="https://policies.google.com/privacy">
              Google
            </a>{" "}
            for details.
          </li>
          <li>
            <strong>If required by law</strong> — for example, a lawful court order or
            regulatory request under New Zealand law.
          </li>
        </ul>

        <h2 className="mt-10 font-display font-bold text-xl">5. How long we keep your data</h2>
        <ul className="mt-2 list-disc pl-6 space-y-1.5">
          <li>
            Form submissions (demo requests, contact messages, early-access entries) are
            kept until you ask us to delete them, or until they are no longer needed for the
            purpose they were collected.
          </li>
          <li>Anonymous website visit records are retained in aggregate for up to 24 months.</li>
          <li>
            Records you capture in the Arvo mobile app remain on your device for as long as
            you choose to keep them. Uninstalling the app removes them from your device.
          </li>
        </ul>

        <h2 className="mt-10 font-display font-bold text-xl">6. Your rights</h2>
        <p className="mt-2">
          Under the New Zealand Privacy Act 2020 you have the right to:
        </p>
        <ul className="mt-2 list-disc pl-6 space-y-1.5">
          <li>Ask what personal information we hold about you</li>
          <li>Ask for it to be corrected if it is wrong</li>
          <li>Ask for it to be deleted</li>
          <li>
            Withdraw your consent to receive marketing emails (such as the early-access
            notification) at any time
          </li>
          <li>
            Complain to the Office of the Privacy Commissioner — see{" "}
            <a className="text-[#2D4A22] font-semibold" href="https://www.privacy.org.nz">
              privacy.org.nz
            </a>{" "}
            for details
          </li>
        </ul>
        <p className="mt-3">
          To exercise any of these rights, email{" "}
          <a className="text-[#2D4A22] font-semibold" href="mailto:support@getarvo.co.nz">
            support@getarvo.co.nz
          </a>
          . We will respond within 20 working days.
        </p>

        <h2 className="mt-10 font-display font-bold text-xl">7. Security</h2>
        <p className="mt-2">
          We take reasonable steps to protect personal information from loss, misuse and
          unauthorised access. This includes encrypted data transfer (HTTPS) on the
          website, access controls for our internal systems, and storing the small amount
          of information we hold in a secure, password-protected database. No system is
          ever 100% secure, however, so we encourage you to use a strong, unique password
          if you ever create accounts elsewhere that interact with Arvo.
        </p>

        <h2 className="mt-10 font-display font-bold text-xl">8. Children&apos;s privacy</h2>
        <p className="mt-2">
          Arvo is a professional tool intended for use by adults working in livestock
          operations. The Service is not directed to children under 16, and we do not
          knowingly collect personal information from children under 16. If you believe a
          child has provided us information, please contact{" "}
          <a className="text-[#2D4A22] font-semibold" href="mailto:support@getarvo.co.nz">
            support@getarvo.co.nz
          </a>{" "}
          and we will delete it.
        </p>

        <h2 className="mt-10 font-display font-bold text-xl">9. Changes to this Policy</h2>
        <p className="mt-2">
          We may update this Privacy Policy from time to time to reflect changes in our
          practices or for legal reasons. When we make a material change we will update the
          &quot;Last updated&quot; date at the top of this page, and — for material changes
          affecting the mobile app — we will surface a notice in the app on next launch.
          Continued use of the Service after a change becomes effective means you accept
          the updated policy.
        </p>

        <h2 className="mt-10 font-display font-bold text-xl">10. Contact us</h2>
        <p className="mt-2">
          Questions, concerns or requests about this Privacy Policy? Email us at{" "}
          <a className="text-[#2D4A22] font-bold" href="mailto:support@getarvo.co.nz">
            support@getarvo.co.nz
          </a>
          .
        </p>

        <p className="mt-12 pt-6 border-t border-[#D5D1C8] text-sm text-[#4A4F4A]">
          Arvo · Independent &amp; provider-neutral livestock data capture · Built in New
          Zealand ·{" "}
          <a className="text-[#2D4A22] font-semibold" href="/">
            getarvo.co.nz
          </a>
        </p>
      </article>
    </main>
  );
}
